import React from "react";
import { useNavigate } from "react-router-dom";

import { psychologistTestsApi } from "../../api/psychologistTestsApi";
import { useToast } from "../../ui/toast/ToastProvider";

function formatInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("ru-RU");
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function statusLabel(status) {
  const token = String(status || "").toUpperCase();
  if (token === "PUBLISHED") return "Опубликован";
  if (token === "ARCHIVED") return "Архив";
  return "Черновик";
}

function statusClass(status) {
  const token = String(status || "").toUpperCase();
  if (token === "PUBLISHED") return "is-published";
  return "is-draft";
}

function assignmentStatusLabel(status) {
  const token = String(status || "").toUpperCase();
  if (token === "COMPLETED") return "Пройден";
  if (token === "IN_PROGRESS") return "В процессе";
  if (token === "CANCELLED") return "Отменён";
  if (token === "OVERDUE") return "Просрочен";
  return "Назначен";
}

function assignmentStatusClass(status) {
  const token = String(status || "").toUpperCase();
  if (token === "COMPLETED") return "is-published";
  if (token === "IN_PROGRESS") return "is-draft";
  return "";
}

function iconTone(idx) {
  const tones = ["is-purple", "is-amber", "is-teal", "is-coral", "is-pink"];
  return tones[idx % tones.length];
}

function sortAssignments(items = []) {
  return [...items].sort((a, b) => {
    const aTs = new Date(a.deadlineAt || a.createdAt || 0).getTime() || 0;
    const bTs = new Date(b.deadlineAt || b.createdAt || 0).getTime() || 0;
    return bTs - aTs;
  });
}

export default function PsychologistTestsPanel() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = React.useState(true);
  const [publishingId, setPublishingId] = React.useState(null);
  const [tests, setTests] = React.useState([]);
  const [assignments, setAssignments] = React.useState([]);
  const assignmentsRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [testsPage, assignmentsPage] = await Promise.all([
        psychologistTestsApi.listMine({ page: 0, size: 8 }),
        psychologistTestsApi.listAssignments({ page: 0, size: 8 }),
      ]);

      const metricsById = new Map();
      await Promise.allSettled(
        testsPage.items.map(async (item) => {
          if (!item?.id) return;
          const metrics = await psychologistTestsApi.getMetrics(item.id);
          metricsById.set(item.id, metrics);
        })
      );

      setTests(
        testsPage.items.map((item) => ({
          ...item,
          metrics: metricsById.get(item.id) || null,
        }))
      );
      setAssignments(sortAssignments(assignmentsPage.items));
    } catch (err) {
      toast.error(err?.message || "Не удалось загрузить тесты");
      setTests([]);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  React.useEffect(() => {
    load();
  }, [load]);

  const stats = React.useMemo(() => {
    const published = tests.filter((item) => item.status === "PUBLISHED").length;
    const drafts = tests.filter((item) => item.status !== "PUBLISHED").length;
    const inProgress = assignments.filter((item) => item.status === "IN_PROGRESS").length;
    const completed = assignments.filter((item) => item.status === "COMPLETED").length;
    return {
      published,
      drafts,
      assignments: assignments.length,
      inProgress,
      completed,
    };
  }, [tests, assignments]);

  const handlePublish = React.useCallback(
    async (test) => {
      if (!test?.id || publishingId) return;
      setPublishingId(test.id);
      try {
        const updated = await psychologistTestsApi.publish(test.id);
        setTests((current) =>
          current.map((item) =>
            item.id === updated.id
              ? {
                  ...item,
                  ...updated,
                }
              : item
          )
        );
        toast.success("Тест опубликован");
      } catch (err) {
        toast.error(err?.message || "Не удалось опубликовать тест");
      } finally {
        setPublishingId(null);
      }
    },
    [publishingId, toast]
  );

  const scrollToAssignments = React.useCallback(() => {
    assignmentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const latestAssignment = assignments[0] || null;

  return (
    <section className="psy-tests">
      <header className="psy-tests__head">
        <div className="psy-tests__head-left">
          <h2>Тесты психолога</h2>
          <p>
            {loading
              ? "Загружаем библиотеку тестов и последние назначения."
              : `${formatInt(stats.published)} опубликовано · ${formatInt(stats.assignments)} назначений клиентам`}
          </p>
        </div>
        <div className="psy-tests__head-actions">
          <button type="button" className="dashboard__btn dashboard__btn--ghost" onClick={() => navigate("/dashboard")}>
            В кабинет
          </button>
          <button type="button" className="dashboard__btn dashboard__btn--main" onClick={scrollToAssignments}>
            Назначения
          </button>
        </div>
      </header>

      <div className="psy-tests__stats">
        <article className="psy-tests__stat-card">
          <div className="psy-tests__stat-val">{loading ? "—" : formatInt(stats.published)}</div>
          <div className="psy-tests__stat-lbl">тестов опубликовано</div>
        </article>
        <article className="psy-tests__stat-card">
          <div className="psy-tests__stat-val">{loading ? "—" : formatInt(stats.drafts)}</div>
          <div className="psy-tests__stat-lbl">черновиков</div>
        </article>
        <article className="psy-tests__stat-card">
          <div className="psy-tests__stat-val">{loading ? "—" : formatInt(stats.inProgress)}</div>
          <div className="psy-tests__stat-lbl">клиенты проходят сейчас</div>
        </article>
        <article className="psy-tests__stat-card">
          <div className="psy-tests__stat-val">{loading ? "—" : formatInt(stats.completed)}</div>
          <div className="psy-tests__stat-lbl">успешно завершено</div>
        </article>
      </div>

      {latestAssignment ? (
        <div className="psy-tests__assignment-banner">
          <div className="psy-tests__assignment-banner-icon">🧪</div>
          <div className="psy-tests__assignment-banner-body">
            <div className="psy-tests__assignment-banner-title">{latestAssignment.testTitle}</div>
            <div className="psy-tests__assignment-banner-meta">
              {latestAssignment.clientName} · {assignmentStatusLabel(latestAssignment.status)}
              {latestAssignment.deadlineAt ? ` · дедлайн ${formatDate(latestAssignment.deadlineAt)}` : ""}
            </div>
          </div>
          <button type="button" className="dashboard__btn dashboard__btn--ghost" onClick={scrollToAssignments}>
            К назначениям
          </button>
        </div>
      ) : null}

      <div className="psy-tests__list">
        {loading ? <div className="empty-note">Загружаем список тестов…</div> : null}
        {!loading && tests.length === 0 ? <div className="empty-note">Тестов пока нет. Как только они появятся на бэкенде, список заполнится автоматически.</div> : null}

        {!loading &&
          tests.map((item, idx) => (
            <article key={item.id || idx} className="psy-tests__item">
              <div className={`psy-tests__icon ${iconTone(idx)}`}>📝</div>

              <div className="psy-tests__body">
                <div className="psy-tests__name-row">
                  <h3>{item.title}</h3>
                  <span className={`psy-tests__status ${statusClass(item.status)}`}>{statusLabel(item.status)}</span>
                </div>

                <p className="psy-tests__desc">{item.topic || "Тематика будет подтянута из карточки теста."}</p>

                <div className="psy-tests__meta">
                  <span>📋 {formatInt(item.questionsCount)} вопросов</span>
                  {item.estimatedMinutes > 0 ? <span>⏱ {formatInt(item.estimatedMinutes)} мин</span> : null}
                  <span>🔄 {formatInt(item.metrics?.assignmentsCount || 0)} назначений</span>
                  <span className="is-teal">✅ {formatInt(item.metrics?.completedAssignmentsCount || 0)} завершено</span>
                  <span>🗓 Обновлён {formatDate(item.updatedAt)}</span>
                </div>
              </div>

              <div className="psy-tests__right">
                <div className="psy-tests__count">{formatInt(item.metrics?.completionRate || 0)}%</div>
                <div className="psy-tests__count-lbl">завершения</div>

                <div className="psy-tests__actions">
                  <button type="button" className="dashboard__btn dashboard__btn--ghost" onClick={scrollToAssignments}>
                    Назначения
                  </button>
                  {item.status === "PUBLISHED" ? (
                    <button type="button" className="dashboard__btn dashboard__btn--main" onClick={() => navigate("/chat")}>
                      Чат с клиентами
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="dashboard__btn dashboard__btn--main"
                      onClick={() => handlePublish(item)}
                      disabled={publishingId === item.id}
                    >
                      {publishingId === item.id ? "Публикация..." : "Опубликовать"}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
      </div>

      <div ref={assignmentsRef} className="psy-tests__assignments">
        <div className="psy-tests__assignments-head">
          <h3>Последние назначения</h3>
          <button type="button" className="dashboard__btn dashboard__btn--ghost" onClick={load}>
            Обновить
          </button>
        </div>

        {loading ? <div className="empty-note">Загружаем назначения…</div> : null}
        {!loading && assignments.length === 0 ? <div className="empty-note">Назначений клиентам пока нет.</div> : null}

        {!loading &&
          assignments.map((item) => (
            <div key={item.id || `${item.testId}-${item.clientUserId}`} className="psy-tests__assignment-row">
              <div className="psy-tests__assignment-main">
                <div className="psy-tests__assignment-title">{item.testTitle}</div>
                <div className="psy-tests__assignment-meta">
                  {item.clientName}
                  {item.deadlineAt ? ` · дедлайн ${formatDate(item.deadlineAt)}` : item.createdAt ? ` · создано ${formatDate(item.createdAt)}` : ""}
                </div>
              </div>
              <span className={`psy-tests__status ${assignmentStatusClass(item.status)}`}>{assignmentStatusLabel(item.status)}</span>
            </div>
          ))}
      </div>
    </section>
  );
}
